import { BackButton, Loader } from "@egovernments/digit-ui-react-components";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { convertEpochToDateDMY } from "../../../utils";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TablePagination from "@mui/material/TablePagination";
import TableSortLabel from "@mui/material/TableSortLabel";
import { Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, Popover, TextField, Typography } from "@mui/material";
import DialogContentModal from "../../../../../templates/ApplicationDetails/components/DialogContent";
import useDepartmentSelector from "../../citizen/TCPForms/hooks/useDepartmentSelector";
import SearchSummary from "../AVPForms/formComponents/ltp-registration/Summary/SearchSummary";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { citizenBaseUrl } from "../AVPForms/components/data";

const ApplicationProfessional = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [smShowMs, setSmShowMs] = useState(false);
  const [idData, setIdData] = useState("");
  const [formType, setFormType] = useState("");
  const tenantId = Digit.ULBService.getStateId() || "up";
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [page, setPage] = React.useState(0);
  const [globalSearch, setGlobalSearch] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedColumns, setSelectedColumns] = useState([
    "srNo",
    "name",
    "mobileNumber",
    "applicationNumber",
    "applicationType",
    "applicationDate",
    "status",
    "approvalLetter",
    "paymentReceipt",
    "action",
  ]);
  const [filters, setFilters] = useState({
    name: "",
    mobileNumber: "",
    applicationNumber: "",
    applicationType: "",
    status: "",
  });
  const [orderBy, setOrderBy] = useState("applicationDate");
  const [order, setOrder] = useState("desc");

  const [isUdFlow, applicationFlow, DepartmentSelector] = useDepartmentSelector();
  const isEmployee = window.location.pathname.split("/").includes("employee");

  const { data, isLoading: loader, revalidate } = Digit.Hooks.obps.useBPAREGSearch(tenantId, undefined, undefined, undefined, undefined, undefined, {
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: true,
    // refetchOnWindowFocus: true,
    // refetchOnReconnect: true,
  });

  // Sorting function
  const getComparator = (order, orderBy) => {
    return (a, b) => {
      const getValue = (item) => {
        switch (orderBy) {
          case "name":
            return item?.tradeLicenseDetail?.owners?.[0]?.name?.toLowerCase() || "";
          case "mobileNumber":
            return item?.tradeLicenseDetail?.owners?.[0]?.mobileNumber || "";
          case "applicationNumber":
            return item?.applicationNumber?.toLowerCase() || "";
          case "applicationType":
            return t(item?.applicationType)?.toLowerCase() || "";
          case "status":
            return t(`WF_BPA_11_${item?.status}`)?.toLowerCase() || "";
          case "applicationDate":
            return item?.applicationDate || 0;
          default:
            return "";
        }
      };

      const aValue = getValue(a);
      const bValue = getValue(b);

      if (order === "desc") {
        return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    };
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const handleColumnToggle = (columnId) => {
    if (selectedColumns.includes(columnId)) {
      if (selectedColumns.length > 1) {
        setSelectedColumns(selectedColumns.filter((id) => id !== columnId));
      }
    } else {
      setSelectedColumns([...selectedColumns, columnId]);
    }
  };

  const handleFilterChange = (property, value) => {
    setFilters({
      ...filters,
      [property]: value,
    });
    setPage(0);
  };

  const handleClearFilter = (property) => {
    setFilters({
      ...filters,
      [property]: "",
    });
  };

  const matchesSearch = (value, search) => {
    if (!value) return false;
    return value.toString().toLowerCase().includes(search.toLowerCase());
  };

  // Filtering
  const getFilteredData = () => {
    let filteredData = data?.Licenses || [];
    if (globalSearch) {
      filteredData = filteredData.filter((item) => {
        return (
          matchesSearch(item?.tradeLicenseDetail?.owners?.[0]?.name, globalSearch) ||
          matchesSearch(item?.tradeLicenseDetail?.owners?.[0]?.mobileNumber, globalSearch) ||
          matchesSearch(item?.applicationNumber, globalSearch) ||
          matchesSearch(item?.applicationType, globalSearch) ||
          matchesSearch(item?.status, globalSearch)
        );
      });
    }
    if (filters.name) {
      filteredData = filteredData.filter((item) => item?.tradeLicenseDetail?.owners?.[0]?.name?.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.mobileNumber) {
      filteredData = filteredData.filter((item) => item?.tradeLicenseDetail?.owners?.[0]?.mobileNumber?.includes(filters.mobileNumber));
    }
    if (filters.applicationNumber) {
      filteredData = filteredData.filter((item) => item?.applicationNumber?.toLowerCase().includes(filters.applicationNumber.toLowerCase()));
    }
    if (filters.applicationType) {
      filteredData = filteredData.filter((item) => t(item?.applicationType)?.toLowerCase().includes(filters.applicationType.toLowerCase()));
    }
    if (filters.status) {
      filteredData = filteredData.filter((item) => t(`WF_BPA_11_${item?.status}`)?.toLowerCase().includes(filters.status.toLowerCase()));
    }
    return filteredData;
  };

  const filteredData = getFilteredData();
  const sortedData = filteredData.slice().sort(getComparator(order, orderBy));
  const paginatedData = sortedData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const handleClickAction = (item) => {
    history.push(`${citizenBaseUrl}/payment/collect/${item?.businessService}/${item?.applicationNumber}/${tenantId}/tenantId=${tenantId}`);
  };
  const allColumns = [
    {
      id: "srNo",
      label: "Sr No",
      filterable: false,
      width: "5%",
      renderCell: (item, index, page, rowsPerPage) => <td className="td-srno">{page * rowsPerPage + index + 1}</td>,
    },
    {
      id: "name",
      label: "Name",
      filterable: true,
      sortable: true,
      width: "12%",
      renderCell: (item) => <td className="td-name">{item?.tradeLicenseDetail?.owners?.[0]?.name || "-"}</td>,
    },
    {
      id: "mobileNumber",
      label: "Mobile No.",
      filterable: true,
      sortable: true,
      width: "10%",
      renderCell: (item) => <td className="td-mobile">{item?.tradeLicenseDetail?.owners?.[0]?.mobileNumber || "-"}</td>,
    },
    {
      id: "applicationNumber",
      label: "Application No.",
      filterable: true,
      sortable: true,
      width: "10%",
      renderCell: (item) => (
        <td className="td-appno">
          <strong style={{ color: "#007bff", textDecoration: "none", cursor: "pointer" }} onClick={() => closeModal2(item)}>
            {item?.applicationNumber || "-"}
          </strong>
        </td>
      ),
    },
    {
      id: "applicationType",
      label: "Application Type",
      filterable: true,
      sortable: true,
      width: "10%",
      renderCell: (item) => <td className="td-apptype">{t(item?.applicationType) || "-"}</td>,
    },
    {
      id: "applicationDate",
      label: "Date",
      filterable: false,
      sortable: true,
      width: "10%",
      renderCell: (item) => <td className="td-date">{convertEpochToDateDMY(item?.applicationDate) || "-"}</td>,
    },
    {
      id: "status",
      label: "Status",
      filterable: true,
      sortable: true,
      width: "13%",
      renderCell: (item) => <td className="td-status">{t(`WF_BPA_11_${item?.status}`) || "-"}</td>,
    },
    {
      id: "approvalLetter",
      label: "Approval Letter",
      filterable: false,
      sortable: false,
      width: "13%",
      renderCell: (item) => (
        <td className="td-approval">
          {item.status === "APPROVED" && (
            <Button
              size="small"
              sx={{ ml: 1, fontSize: "10px", alignContent: "center", display: "flex" }}
              variant="contained"
              color="success"
              onClick={() => window.open(item?.approvalLetterUrl)}
            >
              View
            </Button>
          )}
        </td>
      ),
    },
    {
      id: "paymentReceipt",
      label: "Payment Receipt",
      filterable: false,
      sortable: false,
      renderCell: (item) => (
        <td className="td-receipt">
          {item?.registrationType !== "Architect" &&
            (item?.status === "APPROVED" ||
              item?.status === "APPLY_FOR_DSC" ||
              item?.status === "FORWARD_TO_ND_OFF_AWAS" ||
              item?.status === "FORWARD_TO_NODAL") ? (
            <Button
              size="small"
              className="btn-table-action"
              variant="contained"
              color="primary"
              onClick={() => {
                history.push({
                  pathname: `/digit-ui/citizen/obps/tradelicence/payment/${item?.applicationNumber}/up`,
                });
              }}
            >
              <span>Receipt</span>
            </Button>
          ) : (
            "N/A"
          )}
        </td>
      ),
      width: "10%",
    },
    {
      id: "action",
      label: "Action",
      filterable: false,
      sortable: false,
      width: "13%",
      renderCell: (item) => (
        <td className="td-action">
          {!isEmployee && ["INITIATE", "UPLOAD_DOC", "REVERT_TO_APPLICANT", "SENDBACK_TO_CITIZEN"].includes(item?.status) ? (
            <Button
              size="small"
              sx={{ ml: 1, fontSize: "10px", alignContent: "center", display: "flex" }}
              variant="contained"
              color="primary"
              onClick={() => {
                window.localStorage.setItem("ApplicationStatus", item?.status);
                history.push({
                  pathname: `/digit-ui/citizen/obps/services/ltpPortal/${item?.applicationNumber}`,
                });
              }}
            >
              <span>ReSubmit</span>
            </Button>
          ) : item.status === "MAKE_PAYMENT" ? (
            <Button
              size="small"
              sx={{ ml: 1, fontSize: "10px", alignContent: "center", display: "flex" }}
              variant="contained"
              color="success"
              onClick={() => handleClickAction(item)}
            >
              <span>Pay</span>
            </Button>
          ) : item.status === "REJECTED" ? (
            <Button
              size="small"
              sx={{ ml: 1, fontSize: "10px", alignContent: "center", display: "flex" }}
              variant="contained"
              color="success"
              onClick={() => {
                history.push({
                  pathname: `/digit-ui/citizen/obps/services/ltpPortal/${item?.applicationNumber}`,
                });
              }}
            >
              <span>View</span>
            </Button>
          ) : (
            <Button
              size="small"
              sx={{ ml: 1, fontSize: "10px", alignContent: "center", display: "flex" }}
              variant="contained"
              color="success"
              onClick={() => closeModal2(item)}
            >
              <span>View</span>
            </Button>
          )}
        </td>
      ),
    },
  ];

  const columns = allColumns.filter((col) => selectedColumns.includes(col.id));

  const handleCloseColumnFilter = () => {
    setAnchorEl(null);
  };
  const handleOpenColumnFilter = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const closeModal2 = (item) => {
    Digit.SessionStorage.set("FILE_NO", item?.applicationNumber);
    setIdData(item?.applicationNumber);
    setFormType("form11");
    if (item?.applicationNumber != null) {
      setSmShowMs(true);
    }
  };

  if (applicationFlow === null) {
    return <DepartmentSelector />;
  }

  return (
    <div>
      {loader && <Loader />}
      <BackButton />
      <TableContainer className="citizen-table-container">
        <div>
          <caption className="citizen-table-caption">{t("BPA_MY_APPLICATIONS")}</caption>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              className="bg-white"
              onClick={handleOpenColumnFilter}
              size="small"
              sx={{
                height: "27px",
                fontSize: "12px",
                borderColor: "rgb(31, 74, 124)",
                color: "rgb(31, 74, 124)",
              }}
            >
              {/* ({selectedColumns.length}/{allColumns.length})  */}
              {t("FASTPAS.GLOBAL.FILTERS.TEXT")}({selectedColumns.length === allColumns.length ? "All" : selectedColumns.length}){" "}
            </Button>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search here..."
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ background: "#1f4a7c", color: "#fff", height: "24px", borderRadius: "7px 0px 0px 7px", width: "29px" }} />
                  </InputAdornment>
                ),
                endAdornment: globalSearch && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setGlobalSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                padding: "0px",
                height: "25px",
                "& .MuiInputBase-root": {
                  height: "27px",
                  padding: "0px",
                  backgroundColor: "#fff",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgb(31, 74, 124)",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  fontSize: "12px",
                },
              }}
            />
          </div>
        </div>
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleCloseColumnFilter}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <div className="select-column-popover">
            <Typography variant="subtitle2" gutterBottom>
              Select Columns to Display
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{ padding: "0px" }}
                  checked={selectedColumns.length === allColumns.length}
                  indeterminate={selectedColumns.length > 0 && selectedColumns.length < allColumns.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedColumns(allColumns.map((col) => col.id));
                    } else {
                      // Optionally, keep at least one column selected
                      setSelectedColumns([allColumns[0].id]);
                    }
                  }}
                />
              }
              sx={{ margin: "0px", fontWeight: "bold" }}
              label="Select All"
            />
            {allColumns.map((column) => (
              <div key={column.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      sx={{ padding: "0px" }}
                      checked={selectedColumns.includes(column.id)}
                      onChange={() => handleColumnToggle(column.id)}
                      disabled={column.id === "srNo" && selectedColumns.length === 1}
                    />
                  }
                  sx={{ margin: "0px" }}
                  label={column.label}
                />
              </div>
            ))}
          </div>
        </Popover>
        <Table stickyHeader aria-label="sticky table" className="citizen-application-table">
          <thead>
            <tr className="citizen-header-row">
              {columns.map((column) => (
                <td key={column.id} display="flex" alignitems="center">
                  {column.label}
                  {column.sortable && (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : "asc"}
                      onClick={() => handleSort(column.id)}
                      sx={{
                        color: "white !important",
                        "& .MuiTableSortLabel-icon": {
                          opacity: "1 !important",
                          color: "white !important",
                        },
                        "&:hover": {
                          color: "white !important",
                        },
                      }}
                    />
                  )}
                </td>
              ))}
            </tr>
            <tr>
              {columns.map((column) => (
                <td key={`filter-${column.id}`}>
                  {column.filterable && (
                    <TextField
                      variant="standard"
                      size="small"
                      value={filters[column.id] || ""}
                      onChange={(e) => handleFilterChange(column.id, e.target.value)}
                      InputProps={{
                        endAdornment: filters[column.id] && (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => handleClearFilter(column.id)}>
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiInput-input": {
                          fontSize: "0.75rem",
                        },
                        width: "100%",
                      }}
                    />
                  )}
                </td>
              ))}
            </tr>
          </thead>
          <tbody>
            {loader ? (
              <tr>
                <td colSpan={columns.length + 5} align="center">
                  Loading...
                </td>
              </tr>
            ) : paginatedData?.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 5} align="center">
                  No records found
                </td>
              </tr>
            ) : (
              paginatedData?.map((item, index) => (
                <tr hover key={index}>
                  {columns.map((column) => column.renderCell(item, index, page, rowsPerPage))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 75, 100]}
        component="div"
        count={filteredData?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <DialogContentModal
        title={"Application Details"}
        dataOpen={smShowMs}
        onClose={() => {
          setSmShowMs(false);
        }}
      >
        <SearchSummary id={idData} formType={formType} />
      </DialogContentModal>
    </div>
  );
};

export default ApplicationProfessional;
